import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import type {
  Stripe,
  SetupIntentResult,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import type {
  BeginPaymentMethodSetupInput,
  BeginPaymentMethodSetupPayload,
  FinalizePaymentMethodSetupInput,
  FinalizePaymentMethodSetupPayload,
} from "types";
import {
  BEGIN_PAYMENT_METHOD_SETUP_MUTATION,
  FINALIZE_PAYMENT_METHOD_SETUP_MUTATION,
} from "globals/graphql/paymentMethod.graphql";
import { getErrorMessage } from "moovsErrors/getErrorMessage";
import { clearSetupKey } from "./setupAttemptKey";
import type {
  PaymentMethodSetupHandle,
  PaymentMethodSetupProps,
  PaymentMethodSetupState,
  SavedPaymentMethod,
  SetupAttempt,
  SetupContext,
} from "./types";

type Props = PaymentMethodSetupProps & {
  storageScope: string;
  stripePromise: Promise<Stripe | null>;
  checkStatusBeforeConfirm: boolean;
  onRetry: () => void;
};
const INITIAL_STATE: PaymentMethodSetupState = {
  status: "initializing",
  ready: false,
  complete: false,
  busy: true,
  error: null,
  result: null,
};
const PAYMENT_OPTIONS: StripePaymentElementOptions = {
  business: { name: "Moovs" },
  layout: {
    type: "tabs",
    defaultCollapsed: false,
  },
  wallets: { applePay: "never", googlePay: "never" },
};
const contextIdentity = (context: SetupContext) =>
  JSON.stringify([
    context.surface,
    context.payerId,
    context.requestId,
    context.invoiceId,
    context.checkoutContext,
  ]);

const SetupElement = forwardRef<PaymentMethodSetupHandle, Props>(
  (props, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const [state, setState] = useState(INITIAL_STATE);
    const [saveForFuture, setSaveForFuture] = useState(false);
    const saveForFutureRef = useRef(false);
    saveForFutureRef.current = saveForFuture;
    const [hasSelectedSavedCard, setHasSelectedSavedCard] = useState(false);
    const stateRef = useRef(state);
    const propsRef = useRef(props);
    propsRef.current = props;
    const activeRef = useRef(true);
    const attemptRef = useRef<SetupAttempt>(
      props.initialAttempt ||
        (props.resume
          ? {
              __typename: "BeginPaymentMethodSetupSuccess",
              ...props.resume,
            }
          : undefined)
    );
    const contextRef = useRef<SetupContext>();
    const confirmedIdRef = useRef<string>();
    const retrieveRef = useRef(
      !!props.resume || !!props.initialAttempt || props.checkStatusBeforeConfirm
    );
    const optionsRef = useRef(props.resume ? props.finalizeOptions : undefined);
    const pendingRef = useRef<Promise<SavedPaymentMethod>>();
    const abortRef = useRef<AbortController>();
    const [begin] = useMutation<
      { beginPaymentMethodSetup: BeginPaymentMethodSetupPayload },
      { input: BeginPaymentMethodSetupInput }
    >(BEGIN_PAYMENT_METHOD_SETUP_MUTATION);
    const [finalize] = useMutation<
      { finalizePaymentMethodSetup: FinalizePaymentMethodSetupPayload },
      { input: FinalizePaymentMethodSetupInput }
    >(FINALIZE_PAYMENT_METHOD_SETUP_MUTATION);
    const assertActive = useCallback(() => {
      if (!activeRef.current)
        throw new Error("Payment setup context changed. Please try again.");
    }, []);
    const update = useCallback((patch: Partial<PaymentMethodSetupState>) => {
      if (!activeRef.current || stateRef.current.result) return;
      const next = { ...stateRef.current, ...patch };
      next.busy = ["initializing", "confirming", "finalizing"].includes(
        next.status
      );
      if (next.restartRequired || next.result)
        clearSetupKey(propsRef.current.storageScope);
      stateRef.current = next;
      setState(next);
    }, []);
    const fail = useCallback(
      (error: unknown) => {
        const failure = new Error(
          getErrorMessage(error) ||
            (error instanceof Error
              ? error.message
              : "Unable to save card. Please try again.")
        );
        update({ status: "failed", error: failure.message });
        return failure;
      },
      [update]
    );
    useEffect(() => {
      propsRef.current.onStateChange(state);
    }, [state]);
    useEffect(() => {
      activeRef.current = true;
      props.stripePromise.then((loaded) => {
        if (!loaded)
          fail(new Error("Unable to load Stripe. Please refresh the page."));
      });
      return () => {
        activeRef.current = false;
        abortRef.current?.abort();
      };
    }, [props.stripePromise, fail]);
    useEffect(() => {
      if (props.resume && stripe && elements)
        update({ status: "ready", ready: true, error: null });
    }, [props.resume, stripe, elements, update]);
    useEffect(() => {
      if (state.status !== "initializing") return;
      const timer = setTimeout(
        () =>
          fail(new Error("Card form took too long to load. Please try again.")),
        30000
      );
      return () => clearTimeout(timer);
    }, [state.status, fail]);

    useImperativeHandle(
      ref,
      () => ({
        save: (submittedContext?: SetupContext) => {
          try {
            assertActive();
          } catch (error) {
            return Promise.reject(error);
          }
          if (stateRef.current.result)
            return Promise.resolve(stateRef.current.result);
          if (pendingRef.current) return pendingRef.current;
          const allowRedisplay =
            propsRef.current.context.surface === "profile" ||
            (propsRef.current.canSaveForFutureBookings &&
              saveForFutureRef.current)
              ? "always"
              : "limited";
          const pending = Promise.resolve().then(async () => {
            try {
              if (!stripe || !elements)
                throw new Error(
                  stateRef.current.error ||
                    "Card form is still loading. Please try again."
                );
              const context = submittedContext || propsRef.current.context;
              if (
                contextRef.current &&
                contextIdentity(contextRef.current) !== contextIdentity(context)
              )
                throw new Error(
                  "Payment setup payer changed. Please restart card setup."
                );
              contextRef.current = { ...context };
              const rememberSuccess = (response: SetupIntentResult) => {
                assertActive();
                if (response?.error || !response?.setupIntent)
                  throw new Error(
                    response.error?.message ||
                      "Unable to confirm card setup. Please try again."
                  );
                const intent = response.setupIntent;
                if (intent.id !== attemptRef.current.setupIntentId) {
                  update({ restartRequired: true });
                  throw new Error(
                    "Unexpected card setup. Please reopen the card dialog."
                  );
                }
                if (intent.status === "succeeded") {
                  confirmedIdRef.current = intent.id;
                  retrieveRef.current = false;
                  update({ restartRequired: false });
                } else if (intent.status === "processing") {
                  throw new Error(
                    "Card setup is still processing. Please try again shortly."
                  );
                } else if (intent.status === "canceled") {
                  update({ restartRequired: true });
                  throw new Error(
                    "Card setup was canceled. Please add the card again."
                  );
                }
              };
              update({ status: "confirming", error: null });
              if (
                !attemptRef.current ||
                (!retrieveRef.current && !confirmedIdRef.current)
              ) {
                if (!stateRef.current.ready || !stateRef.current.complete)
                  throw new Error("Please complete your card details.");
                const submitted = await elements.submit();
                assertActive();
                if (submitted.error)
                  throw new Error(
                    submitted.error.message ||
                      "Please complete your card details."
                  );
              }
              if (!attemptRef.current) {
                const controller = new AbortController();
                abortRef.current = controller;
                const { data } = await begin({
                  variables: {
                    input: { ...context, attemptKey: props.attemptKey },
                  },
                  context: { fetchOptions: { signal: controller.signal } },
                });
                assertActive();
                const attempt = data?.beginPaymentMethodSetup?.result;
                if (attempt?.__typename !== "BeginPaymentMethodSetupSuccess")
                  throw new Error(
                    attempt?.reason === "Payment Element is not enabled"
                      ? "Card setup is temporarily unavailable. Please try again shortly."
                      : attempt?.reason ||
                        "Unable to initialize card setup. Please try again."
                  );
                attemptRef.current = attempt;
                propsRef.current.onAttemptCreated?.(attempt);
              }
              const attempt = attemptRef.current;
              if (!confirmedIdRef.current && retrieveRef.current)
                rememberSuccess(
                  await stripe.retrieveSetupIntent(attempt.clientSecret)
                );
              if (!confirmedIdRef.current) {
                if (props.resume) {
                  update({ restartRequired: true });
                  throw new Error(
                    "Card verification did not complete. Close this dialog and add the card again."
                  );
                }
                if (!stateRef.current.ready || !stateRef.current.complete)
                  throw new Error("Please complete your card details.");
                // Reused attempts may still need new field validation; a succeeded setup skips it.
                if (retrieveRef.current) {
                  const submitted = await elements.submit();
                  assertActive();
                  if (submitted.error)
                    throw new Error(
                      submitted.error.message ||
                        "Please complete your card details."
                    );
                }
                optionsRef.current = { ...propsRef.current.finalizeOptions };
                propsRef.current.onBeforeConfirm?.(
                  attempt.setupIntentId,
                  optionsRef.current
                );
                retrieveRef.current = true;
                const { email, phone, ...billingDetails } =
                  propsRef.current.billingDetails;
                rememberSuccess(
                  await stripe.confirmSetup({
                    elements,
                    clientSecret: attempt.clientSecret,
                    confirmParams: {
                      return_url: new URL(
                        props.returnUrl,
                        window.location.origin
                      ).href,
                      payment_method_data: {
                        allow_redisplay: allowRedisplay,
                        billing_details: {
                          ...(context.surface === "profile"
                            ? billingDetails
                            : {}),
                          ...(email ? { email } : {}),
                          ...(phone ? { phone } : {}),
                        },
                      },
                    },
                    redirect: "if_required",
                  })
                );
                if (!confirmedIdRef.current)
                  throw new Error(
                    "Card setup has not completed. Please try again."
                  );
              }
              update({ status: "finalizing", error: null });
              const { data } = await finalize({
                variables: {
                  input: {
                    ...context,
                    ...optionsRef.current,
                    setupIntentId: confirmedIdRef.current,
                  },
                },
              });
              assertActive();
              const result = data?.finalizePaymentMethodSetup?.result;
              if (result?.__typename !== "FinalizePaymentMethodSetupSuccess")
                throw new Error(
                  result?.reason || "Unable to save card. Please try again."
                );
              const method = {
                ...result.paymentMethod,
                clientSecret: attempt.clientSecret,
              };
              update({ status: "saved", result: method, error: null });
              return method;
            } catch (error) {
              throw fail(error);
            } finally {
              pendingRef.current = undefined;
            }
          });
          pendingRef.current = pending;
          return pending;
        },
      }),
      [
        assertActive,
        stripe,
        elements,
        begin,
        finalize,
        fail,
        update,
        props.attemptKey,
        props.resume,
        props.returnUrl,
      ]
    );

    return (
      <>
        {!props.resume && (
          <PaymentElement
            options={{
              ...PAYMENT_OPTIONS,
              wallets: {
                ...PAYMENT_OPTIONS.wallets,
                ...(props.context.surface === "profile" && { link: "never" }),
              },
              fields: {
                billingDetails: {
                  name: props.context.surface === "profile" ? "never" : "auto",
                  email: props.billingDetails.email ? "never" : "auto",
                  phone: props.billingDetails.phone ? "never" : "auto",
                  address: "auto",
                },
              },
              defaultValues: { billingDetails: props.billingDetails },
            }}
            onReady={() =>
              update({ status: "ready", ready: true, error: null })
            }
            onChange={({ complete, value }) => {
              update({ complete });
              setHasSelectedSavedCard(!!value?.payment_method?.id);
              props.onSelectSavedPaymentMethod?.(
                props.savedPaymentMethods?.find(
                  (method) => method.stripeId === value?.payment_method?.id
                )
              );
            }}
            onLoadError={({ error }) => {
              update({ ready: false, complete: false });
              fail(
                new Error(
                  error.message ||
                    "Unable to load card fields. Please reopen the card dialog."
                )
              );
            }}
          />
        )}
        {!props.resume &&
          props.context.surface !== "profile" &&
          props.canSaveForFutureBookings &&
          !props.savedCardsOnly &&
          !hasSelectedSavedCard && (
            <FormControlLabel
              sx={{
                m: 0,
                mt: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                  lineHeight: "20px",
                },
              }}
              control={
                <Checkbox
                  sx={{ p: 0 }}
                  checked={saveForFuture}
                  disabled={!state.ready || state.busy || !!state.result}
                  onChange={(_, checked) => setSaveForFuture(checked)}
                />
              }
              label="Save this payment method to your profile for future bookings"
            />
          )}
        {state.status === "initializing" && (
          <Typography>Loading card form…</Typography>
        )}
        {state.error && <Alert severity="error">{state.error}</Alert>}
        {state.status === "failed" &&
          !state.ready &&
          !props.resume &&
          !state.restartRequired &&
          !confirmedIdRef.current && (
            <Button onClick={props.onRetry}>Retry card form</Button>
          )}
      </>
    );
  }
);
SetupElement.displayName = "SetupElement";
export default SetupElement;
